export const BOOKING_STATUS = {
  PENDING: 'pending',
  PENDING_PAYMENT: 'pending_payment',
  CONFIRMED: 'confirmed',
  CHECKED_IN: 'checked_in',
  CHECKED_OUT: 'checked_out',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
} as const;

export type BookingStatus = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS];

/** Allowed admin/API transitions for `bookings.status` (cron and internal services bypass this). */
export const VALID_STATUS_TRANSITIONS: Record<string, string[]> = {
  pending_payment: ['confirmed', 'cancelled'],
  pending: ['confirmed', 'cancelled', 'no_show'],
  confirmed: ['checked_in', 'cancelled', 'no_show'],
  checked_in: ['checked_out'],
  checked_out: [],
  cancelled: [],
  no_show: [],
};

/**
 * Same numeric window used for:
 * - Early guest check-in (allowed from this many hours before `bookings.start_datetime`).
 * - Guest berth bookings via widget/API: `start_datetime` must not be earlier than this many hours before “now” (retro / walk-up bookings).
 */
export const CHECK_IN_EARLIEST_HOURS_BEFORE_START = 12;

/**
 * Admin-initiated refunds: not allowed if arrival is sooner than this many hours (stay not started yet).
 * Past stays and checked-out bookings are blocked separately (see admin refund rules).
 */
export const ADMIN_REFUND_MIN_HOURS_BEFORE_ARRIVAL = 48;

export const PAYMENT_STATUS = {
  UNPAID: 'unpaid',
  PENDING_PAYMENT: 'pending_payment',
  PAID: 'paid',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

export const BOOKING_SOURCE = {
  AI_CHAT: 'ai_chat',
  MANUAL_FORM: 'manual_form',
  ADMIN: 'admin',
} as const;

export type BookingSource = (typeof BOOKING_SOURCE)[keyof typeof BOOKING_SOURCE];

export const BERTH_TYPE = {
  VISITOR: 'visitor',
  SEASONAL: 'seasonal',
  BOTH: 'both',
} as const;

export type BerthType = (typeof BERTH_TYPE)[keyof typeof BERTH_TYPE];

export const SUBSCRIPTION_TIER = {
  /** Booking form + online payments; no AI chat (revenue is platform % on guest payments). */
  FREE: 'free',
  STARTER: 'starter',
  GROWTH: 'growth',
  GROUP: 'group',
  ENTERPRISE: 'enterprise',
} as const;

export type SubscriptionTier = (typeof SUBSCRIPTION_TIER)[keyof typeof SUBSCRIPTION_TIER];

export const SUBSCRIPTION_STATUS = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

/** Monthly caps are **new conversations** (`chat_count`), not individual messages — see usage tracking. */
export const SUBSCRIPTION_TIER_CHAT_LIMITS: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 150,
  growth: 500,
  group: 1500,
  enterprise: 30000,
} as const;

/** Max active marinas per org for each SaaS tier (enforced in POST /admin/org/marinas). */
export const SUBSCRIPTION_TIER_MAX_MARINAS: Record<SubscriptionTier, number> = {
  free: 1,
  starter: 1,
  growth: 3,
  group: 10,
  enterprise: 100,
} as const;

/** Higher number = higher product tier (used when multiple Stripe subscriptions are active). */
export const SUBSCRIPTION_TIER_RANK: Record<SubscriptionTier, number> = {
  free: -1,
  starter: 0,
  growth: 1,
  group: 2,
  enterprise: 3,
} as const;

/** `free` and any misconfigured/null tier — no widget AI chat (fail-closed). */
export function subscriptionTierIncludesAiChat(tier: string | null | undefined): boolean {
  if (!tier) return false;
  const n = SUBSCRIPTION_TIER_CHAT_LIMITS[tier as SubscriptionTier];
  return typeof n === 'number' && n > 0;
}

/** Canonical optional module for car / caravan / motorhome parking tools. */
export const MODULE_NAME = {
  PARKING: 'parking',
} as const;

/** DB `marina_modules.module` — `parking` is canonical; legacy keys may still exist until migrated. */
export type ModuleName =
  | (typeof MODULE_NAME)[keyof typeof MODULE_NAME]
  | 'caravan'
  | 'sauna_booking'
  | 'bike_rental'
  | 'weather';

export const KNOWLEDGE_SECTION = {
  SERVICES: 'services',
  RULES: 'rules',
  PRICING: 'pricing',
  FAQ: 'faq',
  FACILITIES: 'facilities',
  EMERGENCY: 'emergency',
} as const;

export type KnowledgeSection = (typeof KNOWLEDGE_SECTION)[keyof typeof KNOWLEDGE_SECTION];

export const CODE_TYPE = {
  WC: 'wc',
  SHOWER: 'shower',
  SAUNA: 'sauna',
  WIFI: 'wifi',
  GATE: 'gate',
} as const;

export type CodeType = (typeof CODE_TYPE)[keyof typeof CODE_TYPE];

export const ADMIN_ROLE = {
  PLATFORM_SUPERADMIN: 'platform_superadmin',
  ORG_ADMIN: 'org_admin',
  MARINA_ADMIN: 'marina_admin',
  MARINA_STAFF: 'marina_staff',
} as const;

export type AdminRole = (typeof ADMIN_ROLE)[keyof typeof ADMIN_ROLE];

/** Roles that can perform write/config operations on a marina */
export const ADMIN_ROLES_WRITE = [
  'platform_superadmin',
  'org_admin',
  'marina_admin',
] as const satisfies AdminRole[];

/** Roles that can perform operational tasks (bookings, check-in/out) */
export const ADMIN_ROLES_OPS = [
  'platform_superadmin',
  'org_admin',
  'marina_admin',
  'marina_staff',
] as const satisfies AdminRole[];

export const PRICING_UNIT = {
  NIGHT: 'night',
  HOUR: 'hour',
  WEEK: 'week',
} as const;

export type PricingUnit = (typeof PRICING_UNIT)[keyof typeof PRICING_UNIT];

export const PRICING_SEASON = {
  HIGH: 'high',
  LOW: 'low',
} as const;

export type PricingSeason = (typeof PRICING_SEASON)[keyof typeof PRICING_SEASON];

export const SPOT_TYPE = {
  CAR: 'car',
  CARAVAN: 'caravan',
  TRAILER: 'trailer',
} as const;

export type SpotType = (typeof SPOT_TYPE)[keyof typeof SPOT_TYPE];

/** Optional marina extras: sauna, bikes, laundry — admin-defined rows */
export const ADDITIONAL_SERVICE_KIND = {
  SAUNA: 'sauna',
  BIKE_RENTAL: 'bike_rental',
  LAUNDRY: 'laundry',
  CUSTOM: 'custom',
} as const;

export type AdditionalServiceKind =
  (typeof ADDITIONAL_SERVICE_KIND)[keyof typeof ADDITIONAL_SERVICE_KIND];

/** How `price_amount` is interpreted for paid additional services. */
export const ADDITIONAL_SERVICE_PRICING_MODEL = {
  FLAT: 'flat',
  PER_HOUR: 'per_hour',
  PER_DAY: 'per_day',
} as const;

export type AdditionalServicePricingModel =
  (typeof ADDITIONAL_SERVICE_PRICING_MODEL)[keyof typeof ADDITIONAL_SERVICE_PRICING_MODEL];

export const ADDITIONAL_SERVICE_PRICING_MODELS: AdditionalServicePricingModel[] = [
  ADDITIONAL_SERVICE_PRICING_MODEL.FLAT,
  ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR,
  ADDITIONAL_SERVICE_PRICING_MODEL.PER_DAY,
];

export const ADDITIONAL_SERVICE_BOOKING_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export type AdditionalServiceBookingStatus =
  (typeof ADDITIONAL_SERVICE_BOOKING_STATUS)[keyof typeof ADDITIONAL_SERVICE_BOOKING_STATUS];

export const ADDITIONAL_SERVICE_RESERVATION_PAYMENT_STATUS = {
  NOT_APPLICABLE: 'not_applicable',
  UNPAID: 'unpaid',
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
} as const;

export type AdditionalServiceReservationPaymentStatus =
  (typeof ADDITIONAL_SERVICE_RESERVATION_PAYMENT_STATUS)[keyof typeof ADDITIONAL_SERVICE_RESERVATION_PAYMENT_STATUS];

export const ADDITIONAL_SERVICE_RESERVATION_SOURCE = {
  ADMIN: 'admin',
  AI_CHAT: 'ai_chat',
} as const;

export type AdditionalServiceReservationSource =
  (typeof ADDITIONAL_SERVICE_RESERVATION_SOURCE)[keyof typeof ADDITIONAL_SERVICE_RESERVATION_SOURCE];

export const LANGUAGE = {
  ET: 'et',
  EN: 'en',
  FI: 'fi',
  DE: 'de',
} as const;

export type Language = (typeof LANGUAGE)[keyof typeof LANGUAGE];

export const RATE_LIMITS = {
  CHAT: { windowMs: 60_000, max: 20 },
  GENERAL: { windowMs: 60_000, max: 60 },
} as const;

export const MAX_TOOL_CALL_ITERATIONS = 5;

export const CHAT_LIMITS = {
  MAX_MESSAGE_LENGTH: 2000,
  MAX_HISTORY_MESSAGES: 50,
} as const;

export const BOOKING_CODE_PREFIX = 'BK-';
export const BOOKING_CODE_LENGTH = 4;
/** Additional service reservation codes (widget / guest-facing), e.g. XS-A7K2 */
export const EXTRA_RESERVATION_CODE_PREFIX = 'XS-';
export const EXTRA_RESERVATION_CODE_LENGTH = 4;
export const SECRET_TOKEN_LENGTH = 64;
